// ============================================
// LevelProgress — persistent per-level progress caption
// ============================================
//
// A quiet one-line text summary (NO progress bar — that would compete with the
// session "Card X of Y" bar). Always visible (Study + Quiz) so users see how far
// they are in the level, reframing the per-session 10-card cap as progress.
//
// Reads existing SRS counter signals; self-hides until the deck loads.
//   studied = learning + review + mastered  (words advanced past 'new')
// NOT total - newCount: newCount only tracks words with an explicit 'new'
// progress entry, so unstarted words aren't in it — `total - newCount` would
// wrongly report everything as studied on a fresh deck.

import {
  totalWords,
  learningCount,
  reviewCount,
  masteredCount,
  currentLevel,
  reviewsDone,
  studyAccuracy
} from '@/state/signals';

export function LevelProgress() {
  const total = totalWords.value;
  if (total <= 0) return null;

  const mastered = masteredCount.value;
  const studied = Math.min(total, learningCount.value + reviewCount.value + mastered);
  const level = currentLevel.value;
  // Lifetime, persistent numbers — derived from wordProgress (saved AND
  // cloud-synced), so a reload never reads as "0 / lost" the way the
  // ephemeral "This session" counters do.
  const reviews = reviewsDone.value;
  const accuracyPct = Math.round(studyAccuracy.value * 100);

  return (
    <p
      className="mb-3 text-xs text-slate-500"
      aria-label={`${level} progress: ${studied} of ${total} words studied${mastered > 0 ? `, ${mastered} mastered` : ''}, ${reviews} lifetime reviews${reviews > 0 ? `, ${accuracyPct}% accuracy` : ''}`}
    >
      <span className="font-semibold text-slate-600">{level}</span>
      {' · '}
      <span>{studied} / {total} studied</span>
      {/* Mastered is a milestone — show it once earned, not a deflating "0". */}
      {mastered > 0 && (
        <>
          {' · '}
          <span className="font-medium text-emerald-600">{mastered} mastered</span>
        </>
      )}
      {' · '}
      <span className="font-semibold text-slate-700">{reviews} reviews</span>
      {reviews > 0 && (
        <>
          {' · '}
          <span className="font-semibold text-indigo-600">{accuracyPct}% accuracy</span>
        </>
      )}
    </p>
  );
}
